import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import '../styles/GraphView.css';
import '../Structures';
import { AutoSizer } from 'react-virtualized';
import { ReactSVGPanZoom } from 'react-svg-pan-zoom';
import Node from './Node';
import TemporaryNode from './TemporaryNode';
import EdgePolyLine from './EdgePolyLine';
import SeparatorLine from './SeperatorLine';
import config from '../config';

class GraphView extends PureComponent {
    static orderEdgesForHighlighting(edges, highlightedEdgesIds) {
        if (highlightedEdgesIds.length === 0) {
            return;
        }
        edges.sort((firstEdge, secondEdge) => {
            const firstValue = highlightedEdgesIds.includes(firstEdge.id) ? 1 : 0;
            const secondValue = highlightedEdgesIds.includes(secondEdge.id) ? 1 : 0;
            return firstValue - secondValue;
        });
    }

    constructor(props) {
        super(props);
        this.state = {
            pathStartX: 0,
            pathStartY: 0,
            pathEndX: 0,
            pathEndY: 0,
            paintingPath: false,
            connectingEdge: null,
            highlightedNodesIds: [],
            highlightedEdgesIds: [],
            isPanning: false,
            panStartX: 0,
            panStartY: 0,
        };
        this.connectingEdge = false;
        this.graphViewContainer = React.createRef();
    }

    handleContextMenu = (event) => {
        event.preventDefault(); // Prevent the default right-click menu
    };

    // handleMouseDown = (event) => {
    //     if (event.button === 2) {  // Right-click
    //         event.preventDefault(); // Prevent default action
    //         this.setState({
    //             isPanning: true,
    //             panStartX: event.clientX,
    //             panStartY: event.clientY,
    //         }, () => {
    //             // Apply the cursor style when panning starts
    //             if (this.graphViewContainer.current) {
    //                 this.graphViewContainer.current.classList.add('cursor-panning');
    //             }
    //         });
    //     }
    // };

    handleMouseDown = (event) => {
        const { readonly } = this.props;
        if (readonly) {
            return;
        }
        const { x, y } = event;

        const edgeOnPoint = this.getEdgeLabelOnPoint(x, y);
        if (!edgeOnPoint) {
            return;
        }

        this.setState({
            pathStartX: x,
            pathStartY: y,
            pathEndX: x,
            pathEndY: y,
            connectingEdge: edgeOnPoint,
            paintingPath: true,
        });
    };

    handleMouseMove = (event) => {
        const { isPanning, panStartX, panStartY } = this.state;
        if (isPanning) {
            const deltaX = event.clientX - panStartX;
            const deltaY = event.clientY - panStartY;
            this.Viewer.pan(deltaX, deltaY); // Pan the view
            this.setState({
                panStartX: event.clientX,
                panStartY: event.clientY,
            });
        }
    };

    // handleMouseUp = (event) => {
    //     if (event.button === 2 && this.state.isPanning) {  // Right-click
    //         this.setState({ isPanning: false }, () => {
    //             // Remove the cursor style when panning ends
    //             if (this.graphViewContainer.current) {
    //                 this.graphViewContainer.current.classList.remove('cursor-panning');
    //             }
    //         });
    //     }
    // };

    handleMouseUp = (event) => {
        const { readonly } = this.props;
        if (readonly) {
            return;
        }
        const { x, y } = event;

        const nodeOnPoint = this.getNodeOnPoint(x, y);


        if (this.state.paintingPath) {
            if (nodeOnPoint && nodeOnPoint.id !== this.state.connectingEdge.source.id) {
                this.props.connectButtonToDestination(this.state.connectingEdge.id, nodeOnPoint.data);
                this.connectingEdge = true;
            }
        }

        this.cleanConnectionState();
    };

    handleOnMouseOver = (event) => {
        const { paintingPath } = this.state;
        if (!paintingPath) {
            this.handleGraphElementsHighlighting(event);
        } else {
            this.handleGraphElementsConnections(event);
        }
    };

    handleMouseClick = (event) => {
        const { readonly, toggleEdgeFoldState } = this.props;
        event.originalEvent.stopPropagation();

        if (this.connectingEdge) {
            this.connectingEdge = false;
            return;
        }
        const { x, y } = event;

        const edgeControlOnPoint = this.getEdgeControlOnPoint(x, y);
        if (!readonly && edgeControlOnPoint && !edgeControlOnPoint.destination) {
            this.props.addTemporaryNode(edgeControlOnPoint.id);
            this.props.cleanSelectedNode();
            return;
        }

        const edgeOnPoint = this.getEdgeLabelOnPoint(x, y);
        if (edgeOnPoint && edgeOnPoint.destination) {
            toggleEdgeFoldState(edgeOnPoint);
            return;
        }

        const nodeOnPoint = this.getNodeOnPoint(x, y);
        if (!nodeOnPoint) {
            this.props.cleanTemporaryNodes();
            this.props.cleanSelectedNode();
            return;
        }

        if (!nodeOnPoint.temporary) {
            this.props.selectNode(nodeOnPoint);
        }
    };

    componentDidMount() {
        const { nodes, rootNodeId } = this.props;
        const rootNode = nodes.find(node => node.id === rootNodeId);
        if (rootNode) {
            const viewCenterY = rootNode.y + rootNode.height / 2;
            this.centerY(viewCenterY);
        }

        // Add mouse event listeners
        window.addEventListener('mousedown', this.handleMouseDown);
        window.addEventListener('mousemove', this.handleMouseMove);
        window.addEventListener('mouseup', this.handleMouseUp);
    }

    componentWillUnmount() {
        // Remove mouse event listeners
        window.removeEventListener('mousedown', this.handleMouseDown);
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
    }

    centerY(y) {
        this.Viewer.pan(0, -y + this.Viewer.props.height / 2);
    }

    getMaxCoordinates = (nodes) => {
        let maxX = 0;
        let maxY = 0;
        for (const node of nodes) {
            const x = node.x + node.width;
            const y = node.y + node.height;
            if (maxX < x) {
                maxX = x;
            }
            if (maxY < y) {
                maxY = y;
            }
        }
        return {
            maxX,
            maxY,
        };
    };

    cleanSelections = () => {
        const { cleanTemporaryNodes, cleanSelectedNode } = this.props;
        cleanTemporaryNodes();
        cleanSelectedNode();
    };

    handleGraphElementsHighlighting = (event) => {
        const { x, y } = event;

        let edgeOnPoint = this.getEdgeControlOnPoint(x, y);
        if (!edgeOnPoint) {
            edgeOnPoint = this.getEdgeLabelOnPoint(x, y);
        }

        if (edgeOnPoint) {
            this.highlightEdge(edgeOnPoint);
            return;
        }

        const nodeOnPoint = this.getNodeOnPoint(x, y);

        if (nodeOnPoint) {
            this.highlightNode(nodeOnPoint);
            return;
        }

        this.clearHighlight();
    };

    handleGraphElementsConnections = (event) => {
        const { x, y } = event;

        const nodeOnPoint = this.getNodeOnPoint(x, y);
        if (nodeOnPoint && nodeOnPoint.id !== this.state.connectingEdge.source.id) {
            this.highlightNode(nodeOnPoint);
        } else {
            this.clearNodeHighlight();
        }

        this.setState({
            pathEndX: x,
            pathEndY: y,
        });
    };

    getNodeOnPoint = (x, y) => {
        let nodeOnPoint = null;
        const { nodes } = this.props;
        for (const node of nodes) {
            if (this.pointBelongsToNode(node, x, y)) {
                nodeOnPoint = node;
                break;
            }
        }
        return nodeOnPoint;
    };

    getEdgeLabelOnPoint = (x, y) => {
        let edgeLabelOnPoint = null;
        const nodeOnPoint = this.getNodeOnPoint(x, y);
        if (!nodeOnPoint) {
            return null;
        }

        for (const edge of nodeOnPoint.edges) {
            if (this.pointBelongsToEdgeLabel(edge, x, y)) {
                edgeLabelOnPoint = edge;
                break;
            }
        }

        return edgeLabelOnPoint;
    };

    getEdgeControlOnPoint = (x, y) => {
        let edgeControlOnPoint = null;
        const { edges } = this.props;

        for (const edge of edges) {
            if (this.pointBelongsToEdgeControl(edge, x, y)) {
                edgeControlOnPoint = edge;
                break;
            }
        }

        return edgeControlOnPoint;
    };

    pointBelongsToNode = (node, x, y) => {
        let belongs = true;
        if (node.x > x || node.x + config.sizes.nodeWidth < x) {
            belongs = false;
        }
        if (node.y > y || node.y + node.height < y) {
            belongs = false;
        }

        return belongs;
    };

    pointBelongsToEdgeLabel = (edge, x, y) => {
        const sourceNode = edge.source;
        let belongs = true;

        const edgeLabelAbsoluteX = sourceNode.x + config.sizes.nodeInnerPadding;
        if (x < edgeLabelAbsoluteX || x > edgeLabelAbsoluteX + config.sizes.nodeWidth - config.sizes.nodeInnerPadding) {
            belongs = false;
        }

        const edgeLabelAbsoluteY = sourceNode.y + config.sizes.nodeHeaderHeight + edge.y;
        if (y < edgeLabelAbsoluteY || y > edgeLabelAbsoluteY + config.sizes.edgeLabelBoxHeight) {
            belongs = false;
        }

        return belongs;
    };

    pointBelongsToEdgeControl = (edge, x, y) => {
        const controlCenterX = edge.source.x + config.sizes.nodeWidth - config.sizes.nodeInnerPadding;
        const controlCenterY = edge.source.y + config.sizes.nodeHeaderHeight + edge.y + config.sizes.edgeLabelInnerBoxHeight / 2;

        const distance = Math.sqrt(Math.pow(controlCenterX - x, 2) + Math.pow(controlCenterY - y, 2));

        return distance <= (config.sizes.addNodeIconHeight / 2);
    };

    highlightEdge = (edge) => {
        const { highlightedEdgesIds, highlightedNodesIds } = this.state;
        if (edge.destination) {
            if (highlightedEdgesIds.includes(edge.id) && highlightedNodesIds.includes(edge.destination.id)) {
                return;
            }

            this.setState({
                highlightedEdgesIds: [edge.id],
                highlightedNodesIds: [edge.destination.id],
            });
        } else {
            if (highlightedEdgesIds.includes(edge.id)) {
                return;
            }

            this.setState({
                highlightedNodesIds: [],
                highlightedEdgesIds: [edge.id],
            });
        }
    };

    highlightNode = (node) => {
        const { highlightedNodesIds } = this.state;
        if (highlightedNodesIds.includes(node.id)) {
            return;
        }

        this.setState({
            highlightedNodesIds: [node.id],
            highlightedEdgesIds: [],
        });
    };

    clearHighlight = () => {
        const { highlightedNodesIds, highlightedEdgesIds } = this.state;
        if (highlightedEdgesIds.length !== 0 || highlightedNodesIds.length !== 0) {
            this.setState({
                highlightedNodesIds: [],
                highlightedEdgesIds: [],
            });
        }
    };

    clearNodeHighlight = () => {
        const { highlightedNodesIds } = this.state;
        if (highlightedNodesIds.length !== 0) {
            this.setState({
                highlightedNodesIds: [],
            });
        }
    };

    cleanConnectionState = () => {
        this.setState({
            pathStartX: 0,
            pathStartY: 0,
            pathEndX: 0,
            pathEndY: 0,
            connectingEdge: null,
            paintingPath: false,
        });
    };

    render() {
        const { nodes, edges, cleanTemporaryNodes, foldedEdgesIds } = this.props;
        const { highlightedNodesIds, highlightedEdgesIds } = this.state;
        GraphView.orderEdgesForHighlighting(edges, highlightedEdgesIds);

        const nodesView = nodes.map((node) => {
            if (node.temporary) {
                return (
                    <TemporaryNode
                        key={node.id}
                        x={node.x}
                        y={node.y}
                        data={node.data}
                        cleanTemporaryNodes={cleanTemporaryNodes}
                    />
                );
            }

            const nodeEdgeIds = node.edges.map(edge => edge.id);
            const nodeHighlightedEdgesIds = highlightedEdgesIds.filter(id => nodeEdgeIds.includes(id));
            const nodeFoldedEdgeIds = foldedEdgesIds.filter(id => nodeEdgeIds.includes(id));
            return (
                <Node
                    key={node.id}
                    x={node.x}
                    y={node.y}
                    height={node.height}
                    name={node.data.name}
                    message={node.data.message}
                    edges={node.edges}
                    terminalMessage={node.data.terminalMessage}
                    includedTypesInAttachments={node.includedTypesInAttachments}
                    chatWithOperator={node.data.chatWithOperator}
                    isHighlighted={highlightedNodesIds.includes(node.id)}
                    highlightedEdgesIds={nodeHighlightedEdgesIds}
                    foldedEdgeIds={nodeFoldedEdgeIds}
                />
            );
        });

        const edgesShapesView = edges.map(edge => (
            <EdgePolyLine
                key={edge.id}
                isHighlighted={highlightedEdgesIds.includes(edge.id)}
                polylinePoints={edge.shapePoints}
            />
        ));

        const separatorLinesView = this.props.layersSeparatorLinesAxises.map((axisValue, index) => (
            <SeparatorLine
                key={index}
                axisValue={axisValue}
            />
        ));

        const path = `M ${this.state.pathStartX} ${this.state.pathStartY} L ${this.state.pathEndX} ${this.state.pathEndY} Z`;

        const { maxX, maxY } = this.getMaxCoordinates(nodes);
        const { width, height } = this.props;
        return (
            <div
                className="graph-view cursor-default" // Add cursor-default to the class list
                onClick={this.cleanSelections}
                onContextMenu={this.handleContextMenu} // Add context menu handler
                ref={this.graphViewContainer} // Attach the ref here
            >
                <ReactSVGPanZoom
                    width={width}
                    height={height}
                    detectAutoPan={false}
                    background="#F7F8F9"
                    miniaturePosition="none"
                    onMouseDown={this.handleMouseDown}
                    onMouseMove={this.handleOnMouseOver}
                    onMouseUp={this.handleMouseUp}
                    onClick={this.handleMouseClick}
                    ref={Viewer => this.Viewer = Viewer}
                >
                    <svg width={width > maxX ? width : maxX} height={height > maxY ? height : maxY}>
                        <g>
                            <rect width={width > maxX ? width : maxX} height={height > maxY ? height : maxY} fill="#F7F8F9" />
                            <g className="nodes-container">
                                {nodesView}
                            </g>
                            <g className="edges-shapes-container">
                                {edgesShapesView}
                            </g>
                            <g>
                                {separatorLinesView}
                            </g>
                            <path d={path} stroke="#2A60E4" strokeWidth="1" fill="none" />
                        </g>
                    </svg>
                </ReactSVGPanZoom>
            </div>
        );
    }
}

class GraphViewAutoSizer extends React.Component {
    render() {
        return (
            <div className="graph-view-autosizer">
                <AutoSizer>
                    {(({ width, height }) => (width === 0 || height === 0 ? null : (
                        <GraphView {...this.props} width={width} height={height} />
                    )))}
                </AutoSizer>
            </div>
        );
    }
}

GraphView.propTypes = {
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    nodes: PropTypes.array.isRequired,
    edges: PropTypes.array.isRequired,
    rootNodeId: PropTypes.number.isRequired,
    readonly: PropTypes.bool.isRequired,
    layersSeparatorLinesAxises: PropTypes.array.isRequired,
    foldedEdgesIds: PropTypes.array.isRequired,
    cleanSelectedNode: PropTypes.func.isRequired,
    selectNode: PropTypes.func.isRequired,
    cleanTemporaryNodes: PropTypes.func.isRequired,
    addTemporaryNode: PropTypes.func.isRequired,
    connectButtonToDestination: PropTypes.func.isRequired,
    toggleEdgeFoldState: PropTypes.func.isRequired,
};

GraphViewAutoSizer.propTypes = {
    nodes: PropTypes.array.isRequired,
    edges: PropTypes.array.isRequired,
    rootNodeId: PropTypes.number.isRequired,
    readonly: PropTypes.bool.isRequired,
    layersSeparatorLinesAxises: PropTypes.array.isRequired,
    foldedEdgesIds: PropTypes.array.isRequired,
    cleanSelectedNode: PropTypes.func.isRequired,
    selectNode: PropTypes.func.isRequired,
    cleanTemporaryNodes: PropTypes.func.isRequired,
    addTemporaryNode: PropTypes.func.isRequired,
    connectButtonToDestination: PropTypes.func.isRequired,
    toggleEdgeFoldState: PropTypes.func.isRequired,
};

export default GraphViewAutoSizer;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/graph/components/GraphView.jsx